import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Award, Target, Heart, ArrowRight, Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { companyApiService } from '../services/companyApi';

interface CompanyProfile {
  name: string;
  slogan: string;
  email: string;
  phone: string;
  address: string;
  logo_url?: string;
  website?: string;
}

const About: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<CompanyProfile>({
    name: 'IRIS SOFTECH',
    slogan: 'Solutions Digitales Innovantes',
    email: 'softechiris@gmail.com',
    phone: '771742350',
    address: 'Medina RUE 27 X 22 ENSUITE'
  });

  useEffect(() => {
    const loadCompanyProfile = async () => {
      try {
        const data = await companyApiService.getCompanyProfilePublic();
        setProfile({
          name: data.name || 'IRIS SOFTECH',
          slogan: data.slogan || 'Solutions Digitales Innovantes',
          email: 'softechiris@gmail.com',
          phone: '771742350',
          address: 'Medina RUE 27 X 22 ENSUITE',
          logo_url: data.logo_url,
          website: data.website
        });
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error);
      }
    };
    
    loadCompanyProfile();
  }, []);

  const values = [
    {
      icon: Target,
      title: 'Excellence technique',
      description: 'Nous utilisons les meilleures pratiques et technologies pour livrer des solutions robustes et performantes.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Nous travaillons en étroite collaboration avec nos clients pour comprendre leurs besoins et objectifs.'
    },
    {
      icon: Award,
      title: 'Qualité',
      description: 'Chaque projet est testé rigoureusement et livré avec la documentation complète et le support nécessaire.'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Notre équipe est passionnée par le développement et l\'innovation technologique.'
    }
  ];


  return (
    <section id="about" className="section-padding bg-gray-50">
      <div className="container-max">
        {/* Introduction */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            À propos d'Iris Softech
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Fondée en 2020, Iris Softech est une agence de développement spécialisée dans la création 
            de solutions digitales sur mesure pour les entreprises de toutes tailles.
          </p>
        </div>

        {/* Notre vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Notre vision</h3>
            <div className="space-y-4 text-gray-600">
              <p>
                Iris Softech a été créée avec une vision simple : démocratiser l'accès aux technologies 
                de pointe pour toutes les entreprises, qu'elles soient startups ou grandes corporations.
              </p>
              <p>
                Depuis notre création, nous avons accompagné plus de 50 entreprises dans leur transformation 
                digitale, en livrant des solutions qui génèrent de la valeur et améliorent leur compétitivité.
              </p>
              <p>
                Notre approche se base sur l'écoute, la collaboration et l'excellence technique pour 
                créer des produits qui dépassent les attentes de nos clients.
              </p>
            </div>
          </div>
          
          <div className="flex flex-col items-center justify-center p-8">
            <img 
              src={profile.logo_url || "/images/irislogobgremoved.png"} 
              alt={`${profile.name} Logo`} 
              className="w-64 h-64 object-contain mb-6"
            />
            <p className="text-center text-xl font-light text-gray-700 italic mb-8">
              {profile.slogan}
            </p>
            <button
              onClick={() => navigate('/aboutirissoftech')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2 hover:scale-105"
            >
              <span>À propos de nous</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Nos valeurs */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">Nos valeurs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-amber-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  {value.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Contactez-nous */}
        <div id="contact" className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Contactez-nous
            </h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Vous avez un projet en tête ? Parlons-en ! Nous sommes là pour vous accompagner dans la réalisation de vos ambitions digitales.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Nos coordonnées */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h4 className="text-2xl font-bold text-gray-900 mb-8">Nos coordonnées</h4>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Email</h5>
                    <a href={`mailto:${profile.email}`} className="text-amber-600 hover:underline">
                      {profile.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Téléphone</h5>
                    <a href={`tel:${profile.phone.replace(/\s/g, '')}`} className="text-gray-600">
                      {profile.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-1">Adresse</h5>
                    <p className="text-gray-600">{profile.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pourquoi choisir Iris Softech */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
              <h4 className="text-2xl font-bold text-gray-900 mb-8">
                Pourquoi choisir Iris Softech ?
              </h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Devis gratuit et sans engagement</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Réponse sous 24h</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Accompagnement personnalisé</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-900">Support technique inclus</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
